import {useAppSelector} from '@hooks/app';
import {IToken} from '@store/types';
import {noop} from '@utils/constants';
import {api} from '../api';

export const TRACK_MODELS = {
    rests: 'rests',
    warehouses: 'warehouses',
} as const;

export const TRACK_EVENTS = {
    /** Поиск товаров в каталоге остатков */
    productSearch: 'product_search',
    /** Открытие модалки остатков по товару */
    productRestsView: 'product_rests_view',
    /** Карточка поставщика в поиске складов */
    supplierSearchCard: 'supplier_search_card',
    /** Страница поставщика (/supplier/:id) */
    supplierPageView: 'supplier_page_view',
    /** Клик по типу товара в карточке поставщика */
    supplierProductTypeClick: 'supplier_product_type_click',
    /** Клик «Показать телефон» в модалке поставщика */
    supplierPhoneReveal: 'supplier_phone_reveal',
    /** Переход на сайт поставщика из модалки */
    supplierWebsiteClick: 'supplier_website_click',
    /** Клик по кнопке «Продать на» (RoboTyre) в хедере */
    robotyreClick: 'robotyre_click',
    /** Заход на сайт */
    siteVisit: 'site_visit',
    /** Успешная авторизация */
    userLogin: 'user_login',
} as const;

export type TrackEvent = (typeof TRACK_EVENTS)[keyof typeof TRACK_EVENTS];

export type TrackParams = {
    event: TrackEvent;
    /** Не передаётся для robotyre_click, site_visit, user_login */
    model?: keyof typeof TRACK_MODELS;
    model_id?: number | string | bigint;
    ref_url?: string | null;
};

const STORAGE_KEYS = {
    lastActivityAt: 'track_last_activity_at',
    lastSiteVisitAt: 'track_last_site_visit_at',
} as const;

const INACTIVITY_MS = 3 * 60 * 60 * 1000;
const SITE_VISIT_MIN_INTERVAL_MS = 24 * 60 * 60 * 1000;

const getSessionId = (): string => {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
        sid = crypto.randomUUID?.() ?? (Date.now().toString(36) + Math.random().toString(36).slice(2));
        localStorage.setItem('session_id', sid);
    }
    return sid;
};

export const trackEvent = (params: TrackParams, authToken?: IToken | null) => {
    const headers: Record<string, string> = {Accept: 'application/json'};
    if (authToken?.token) {
        headers.Authorization = `${authToken.token_type} ${authToken.token}`;
    }

    api.post('/track', {
        event: params.event,
        ...(params.model ? {model: params.model} : {}),
        model_id: params.model_id,
        url: window.location.href,
        ref_url: params.ref_url,
        session_id: getSessionId(),
    }, {headers}).catch(noop);
};

export const shouldTrackSiteVisit = (now = Date.now()): boolean => {
    const lastActivity = Number(localStorage.getItem(STORAGE_KEYS.lastActivityAt));
    const lastVisit = Number(localStorage.getItem(STORAGE_KEYS.lastSiteVisitAt));
    const inactiveLongEnough = !lastActivity || now - lastActivity >= INACTIVITY_MS;
    const visitAllowed = !lastVisit || now - lastVisit >= SITE_VISIT_MIN_INTERVAL_MS;
    return inactiveLongEnough && visitAllowed;
};

export const trackSiteVisitIfNeeded = (): void => {
    if (!shouldTrackSiteVisit()) return;
    trackEvent({event: TRACK_EVENTS.siteVisit});
    localStorage.setItem(STORAGE_KEYS.lastSiteVisitAt, String(Date.now()));
};

export const touchUserActivity = (): void => {
    localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(Date.now()));
};

export const useTrack = () => {
    const token = useAppSelector(state => state.app.token);
    return (params: TrackParams) => trackEvent(params, token);
};
